import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Tooltip, Typography, IconButton, Icon, Dialog, DialogTitle, DialogContent, RadioGroup, FormControlLabel, Radio, Button, useTheme, useMediaQuery } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import CheckMarkIcon from '../../../icons/CheckMark.svg';
import IncompleteIcon from '../../../icons/IncompleteIcon.svg';
import EditIcon from '../../../icons/editIcon.svg';
import DeleteIcon from '../../../icons/deleteIcon.svg';
import { ApplicantV2Dto, UpsertApplicantVehicleV2Dto, UpsertApplicationPetV2Dto } from '@inosio/payments-sdk';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { applicationStyles } from './CustomStyles';
import DeleteModal from './DeleteModal';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import CancelIcon from '../../../icons/cancelIcon.svg';
import { useQuery } from '../../../pmui/transactions/Transactions';
import { useHistory } from 'react-router';
import { useCallback, useEffect, useState } from 'react';
import {
  clearCreateApplicantPetRecord,
  clearCreateApplicantVehicleRecord,
  clearDeletePetRecord,
  clearDeleteVehicleRecord,
  clearUpdateApplicantV2,
  createApplicantPetRecord,
  createApplicantVehicleRecord,
  deleteApplicantPetRecord,
  deleteApplicantVehicleRecord,
  getApplicantAllPetRecords,
  getApplicantAllVehicleRecords,
  getApplicantV2,
  selectApplicantsState,
  updateApplicantV2,
} from '../sagas/applicants/applicantsSlice';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import { ApplicantPetValidator, ApplicantVehicleValidator } from '../validators/ApplicantValidators';

interface PetVehiclePropsType {
  petsValidator: ApplicantPetValidator;
  vehiclesValidator: ApplicantVehicleValidator;
}
export default function PetVehicleDashboard(props: PetVehiclePropsType) {
  const query = useQuery();
  const history = useHistory();
  const hasValidationQueryParam = !!query.get('validation');
  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('sm')); //using material ui media query for consistency
  const classes = applicationStyles();
  const dispatch = useDispatch();
  const { ipaUser } = useSelector(selectAuthState);

  //selectors
  const {
    isUpdatingApplicantFailed,
    isCreatingApplicantPetRecordSuccess,
    isCreatingApplicantPetRecordFailed,
    createdApplicantPetRecord,
    isDeletingApplicantPetRecordSuccess,
    isFetchingApplicantSuccess,
    isFetchingApplicantFailure,
    isUpdatingApplicantSuccess,
    Applicant,
    isCreatingApplicantVehicleRecordSuccess,
    createdApplicantVehicleRecord,
    isDeletingApplicantVehicleRecordSuccess,
    isCreatingApplicantVehicleRecordFailed,
    Application,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isFetchingApplicantAllPetRecordsSuccess,
    isFetchingApplicantAllVehicleRecordsSuccess,
    applicantAllPetRecords,
    applicantAllVehicleRecords,
  } = useSelector(selectApplicantsState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);

  //local states
  const [showPetSection, setShowPetSection] = useState(false);
  const [showVehicleSection, setShowVehicleSection] = useState(false);
  const [hasNoPet, setHasNoPet] = useState<boolean | null>(null); //local state for keeping track of what to show in the radio
  const [hasNoVehicle, setHasNoVehicle] = useState<boolean | null>(null); //local state for keeping track of what to show in the radio
  const [showPetPolicy, setShowPetPolicy] = useState(false);
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);
  const [selectedPetUuid, setSelectedPetUuid] = React.useState('');
  const [selectedVehicleUuid, setSelectedVehicleUuid] = React.useState('');
  const [isDeletePetModalOpen, setIsDeletePetModalOpen] = React.useState(false);
  const [isDeleteVehicleModalOpen, setIsDeleteVehicleModalOpen] = React.useState(false);
  const [doesThisAppHaveSomePets, setDoesThisAppHaveSomePets] = React.useState(false);
  const [doesThisAppHaveSomeVehicles, setDoesThisAppHaveSomeVehicles] = React.useState(false); //boolean flag to help control the grammar of the text to the user.
  const [otherApplicants, setOtherApplicants] = useState<ApplicantV2Dto[]>([]);
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [localError, setLocalError] = useState<string | null>(null);

  //handler for radio button for pets
  const handleUpdateHasPet = (e: any) => {
    const selectedHasPet = e.target.value === 'true';

    if (selectedHasPet) {
      dispatch({
        type: createApplicantPetRecord.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
          createPetDto: {} as UpsertApplicationPetV2Dto,
        },
      });
      dispatch({
        type: updateApplicantV2.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
          updateDto: { HasNoAnimals: false },
        },
      });
      setHasNoPet(false);
    } else {
      dispatch({
        type: updateApplicantV2.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
          updateDto: { HasNoAnimals: true },
        },
      });
      setHasNoPet(true);
    }
  };

  //handler for radio button of vehicles
  const handleUpdateHasVehicle = (e: any) => {
    const selectedHasVehicle = e.target.value === 'true';

    if (selectedHasVehicle) {
      dispatch({
        type: createApplicantVehicleRecord.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
          createVehicleDto: {} as UpsertApplicantVehicleV2Dto,
        },
      });
      dispatch({
        type: updateApplicantV2.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
          updateDto: { HasNoVehicles: false },
        },
      });
      setHasNoVehicle(false);
    } else {
      dispatch({
        type: updateApplicantV2.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
          updateDto: { HasNoVehicles: true },
        },
      });
      setHasNoVehicle(true);
    }
  };

  const handleConfirmDeletePetModal = () => {
    setIsDeletePetModalOpen(false);
    setIsSpinnerModalOpen(true);
    dispatch({
      type: deleteApplicantPetRecord.type,
      payload: {
        applicantUuid: ipaUser?.currentProfile.uuid,
        petUuid: selectedPetUuid,
      },
    });
  };

  const handleConfirmDeleteVehicleModal = () => {
    setIsDeleteVehicleModalOpen(false);
    setIsSpinnerModalOpen(true);
    dispatch({
      type: deleteApplicantVehicleRecord.type,
      payload: {
        applicantUuid: ipaUser?.currentProfile.uuid,
        vehicleUuid: selectedVehicleUuid,
      },
    });
  };

  const handleCreateNewPetRecord = () => {
    dispatch({
      type: createApplicantPetRecord.type,
      payload: {
        applicantUuid: ipaUser?.currentProfile.uuid,
        createPetDto: {} as UpsertApplicationPetV2Dto,
      },
    });
  };

  const handleCreateNewVehicleRecord = () => {
    dispatch({
      type: createApplicantVehicleRecord.type,
      payload: {
        applicantUuid: ipaUser?.currentProfile.uuid,
        createVehicleDto: {} as UpsertApplicantVehicleV2Dto,
      },
    });
  };

  const handleCloseSpinnerModal = useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, []);

  //# region useffects

  //on load fetch the applicant
  useEffect(() => {
    if (ipaUser?.currentProfile.uuid) {
      dispatch({ type: getApplicantAllPetRecords.type, payload: { applicantUuid: ipaUser.currentProfile.uuid } });
      dispatch({ type: getApplicantAllVehicleRecords.type, payload: { applicantUuid: ipaUser.currentProfile.uuid } });
      dispatch({ type: getApplicantV2.type });
    }
  }, [dispatch, ipaUser]);

  //logic for showing dynamic text for pets or vehicles question
  useEffect(() => {
    //applicantAllPetRecords/applicantAllVehicleRecords represents current users
    //Application.[primary]/[coapps] pets/vehicles represent everybody elses
    if (!isFetchingApplicantAllPetRecordsSuccess || !isFetchingApplicantAllVehicleRecordsSuccess || !isFetchingApplicationSuccess) return;
    const allPetsSuperSet = [...(Application.PrimaryApplicant.Pets ?? []), ...Application.CoApplicants.flatMap((ca) => ca.Pets ?? [])];
    const allVehiclesSuperSet = [...(Application.PrimaryApplicant.Vehicles ?? []), ...Application.CoApplicants.flatMap((ca) => ca.Vehicles ?? [])];
    setDoesThisAppHaveSomePets(!!allPetsSuperSet.length);
    setDoesThisAppHaveSomeVehicles(!!allVehiclesSuperSet.length);
    //get all applicants other than the current user
    const allOtherApplicants = [Application.PrimaryApplicant, ...Application.CoApplicants].filter((a) => a.Uuid !== ipaUser?.currentProfile.uuid);
    setOtherApplicants(allOtherApplicants);
  }, [isFetchingApplicationSuccess, isFetchingApplicantAllPetRecordsSuccess, isFetchingApplicantAllVehicleRecordsSuccess, Application]);

  //once applicant is updated fetch a fresh copy
  useEffect(() => {
    if (isUpdatingApplicantSuccess) {
      dispatch(clearUpdateApplicantV2()); //this resets the update states which allows this useffect to not run unnecessarily when we come back to this tab
      dispatch(getApplicantV2());
    }
  }, [dispatch, isUpdatingApplicantSuccess]);

  //set the radio button values once you get applicant data
  useEffect(() => {
    if (isFetchingApplicantSuccess) {
      setHasNoPet(Applicant.HasNoAnimals ?? null);
      setHasNoVehicle(Applicant.HasNoVehicles ?? null);
    }
  }, [Applicant, isFetchingApplicantSuccess]);

  //on load check if the company allows pets/vehicles
  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess) {
      setShowPetSection(!!propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsAllowPets);
      setShowVehicleSection(!!propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsAllowVehicles);
    }
  }, [isFetchingPropertyGroupDetailsSuccess]);

  //when they click add new pet, api request succeeds, listen to this
  useEffect(() => {
    if (isCreatingApplicantPetRecordSuccess && createdApplicantPetRecord?.Uuid) {
      setSelectedPetUuid(createdApplicantPetRecord?.Uuid);
      dispatch(clearCreateApplicantPetRecord());
      history.push(`/resui/applications/${Application.Uuid}/pets/${createdApplicantPetRecord?.Uuid}`);
    }
  }, [isCreatingApplicantPetRecordSuccess, createdApplicantPetRecord]);

  //when they click add new vehicle, api request succeeds, listen to this
  useEffect(() => {
    if (isCreatingApplicantVehicleRecordSuccess && createdApplicantVehicleRecord?.Uuid) {
      setSelectedVehicleUuid(createdApplicantVehicleRecord.Uuid);
      dispatch(clearCreateApplicantVehicleRecord());
      history.push(`/resui/applications/${Application.Uuid}/vehicles/${createdApplicantVehicleRecord?.Uuid}`);
    }
  }, [isCreatingApplicantVehicleRecordSuccess, createdApplicantVehicleRecord]);

  //if they delete a pet record, fetch a fresh copy of application
  useEffect(() => {
    if (isDeletingApplicantVehicleRecordSuccess && ipaUser?.currentProfile.uuid) {
      setSpinnerStatus('stop');
      dispatch(clearDeleteVehicleRecord()); //this resets the delete states which allows this useffect to not run unnecessarily when you navigate back to this page
      dispatch({ type: getApplicantAllVehicleRecords.type, payload: { applicantUuid: ipaUser.currentProfile.uuid } });
    }
  }, [isDeletingApplicantVehicleRecordSuccess, ipaUser]);

  //if they delete a vehicle record, fetch a fresh copy of application
  useEffect(() => {
    if (isDeletingApplicantPetRecordSuccess && ipaUser?.currentProfile.uuid) {
      setSpinnerStatus('stop');
      dispatch(clearDeletePetRecord()); //this resets the delete states which allows this useffect to not run unnecessarily when you navigate back to this page
      dispatch({ type: getApplicantAllPetRecords.type, payload: { applicantUuid: ipaUser.currentProfile.uuid } });
    }
  }, [isDeletingApplicantPetRecordSuccess, ipaUser]);

  //if the pet count changes (ie, they deleted one), and there are none left AND there is a selectedPetUuid (indicating he just clicked delete) then we need to unwind the fact that he selected "i have pets" null
  useEffect(() => {
    if (isFetchingApplicationSuccess && applicantAllPetRecords.length < 1 && selectedPetUuid) {
      dispatch({
        type: updateApplicantV2.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
          updateDto: { HasNoAnimals: null },
        },
      });
    }
  }, [isFetchingApplicationSuccess, applicantAllPetRecords, selectedPetUuid, ipaUser]);

  //if the vehicle count changes (ie, they deleted one), and there are none left AND there is a selectedVehicleUuid (indicating he just clicked delete) then we need to unwind the fact that he selected "i have vehicles" null
  useEffect(() => {
    if (isFetchingApplicationSuccess && applicantAllVehicleRecords && selectedVehicleUuid) {
      dispatch({
        type: updateApplicantV2.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
          updateDto: { HasNoVehicles: null },
        },
      });
    }
  }, [isFetchingApplicationSuccess, applicantAllVehicleRecords, selectedVehicleUuid, ipaUser]);

  //error handling
  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess && isFetchingApplicantSuccess && isFetchingApplicationSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (isFetchingPropertyGroupDetailsFailed || isFetchingApplicantFailure || isFetchingApplicationFailure) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the pets/vehicles information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed, isFetchingApplicantFailure, isFetchingApplicantSuccess, isFetchingApplicationFailure, isFetchingApplicationSuccess]);

  //show error if pet record creation failed
  useEffect(() => {
    if (isCreatingApplicantPetRecordFailed) {
      setLocalError('There was an error creating a pet record. Try refreshing the page');
    }
  }, [isCreatingApplicantPetRecordFailed]);

  //show error if vehicle record creation failed
  useEffect(() => {
    if (isCreatingApplicantVehicleRecordFailed) {
      setLocalError('There was an error creating a vehicle record. Try refreshing the page');
    }
  }, [isCreatingApplicantVehicleRecordFailed]);

  //show error if applicant update failed
  useEffect(() => {
    if (isUpdatingApplicantFailed) {
      setLocalError('There was an error saving your information. Try refreshing the page');
    }
  }, [isUpdatingApplicantFailed]);

  return (
    <Box width="100%" minHeight="60vh">
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: 'inherit', alignItems: 'center' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Box sx={{ width: '100%', p: 4, minHeight: 'inherit' }}>
          {localError && (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
              <Typography className={classes.globalText}>{localError}</Typography>
            </Box>
          )}
          <Typography className={classes.labelText}>{showPetSection && showVehicleSection ? 'Animals & Vehicles' : showPetSection ? 'Animals' : showVehicleSection ? 'Vehicles' : ''}</Typography>

          <Typography sx={{ my: 2 }} className={classes.bodyText}>
            All {showPetSection && showVehicleSection ? 'Animals and Vehicles' : showPetSection ? 'Animals' : showVehicleSection ? 'Vehicles' : ''} that will be on premise, even part-time, must be
            submitted.
          </Typography>

          <Grid container sx={{ mt: 4 }} spacing={2}>
            {showPetSection && (
              <Grid item xs={12} sm={showVehicleSection ? 6 : 12} sx={{ display: 'flex', alignItems: 'center' }}>
                <Box className={classes.cardContainer}>
                  <Box sx={{ px: 1.5 }} className={classes.cardTopContainer}>
                    <Grid container sx={{ width: '100%' }}>
                      <Grid item xs={12} sm={2.5} sx={{ display: 'flex', justifyContent: isOnMobile ? 'center' : 'start', alignItems: 'center' }}>
                        <Typography className={classes.cardLabelText}>
                          Animals
                          <Tooltip title="Details about animals that you live with. Not all properties accept animals, but we will warn you when you apply." placement="right">
                            <HelpIcon sx={{ ml: 0.5, fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
                          </Tooltip>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6.5} sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                        {propertyGroupDetails?.ApplicationsSettings?.ApplicationsPolicyText && (
                          <Typography
                            onClick={() => setShowPetPolicy(true)}
                            sx={{
                              ml: 1,
                              color: '#2E82AF !important',
                              textDecoration: 'underline',
                              fontWeight: '400 !important',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontFamily: 'Work Sans !important',
                            }}
                          >
                            Review our Animal Policies
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: isOnMobile ? 'center' : 'end', alignItems: 'center' }}>
                        <Button
                          type="button"
                          className={classes.wizardAddNewBtn}
                          onClick={() => {
                            handleCreateNewPetRecord();
                          }}
                        >
                          <Typography className={classes.wizardAddNewBtnText} sx={{ margin: '0 10px' }}>
                            {`Add New`}
                          </Typography>
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box sx={{ height: 280, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        marginTop: '1px',
                        height: '100%',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                          width: 7,
                        },
                        '&::-webkit-scrollbar-track': {
                          boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                          borderRadius: '24px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'darkgrey',
                          borderRadius: '24px',
                        },
                      }}
                    >
                      {applicantAllPetRecords.length ? (
                        applicantAllPetRecords?.map((pet) => {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', px: 2, py: 1, borderBottom: '1.5px solid #F1F0F5' }} key={pet.Uuid}>
                              <Box sx={{ width: '100%', pr: 6 }}>
                                <Box sx={{ display: 'flex' }}>
                                  {props.petsValidator?.IsEntireObjectComplete(pet) ? (
                                    <img
                                      style={{
                                        width: '12px',
                                        height: '9px',
                                        marginRight: '11px',
                                        marginTop: '10px',
                                      }}
                                      src={CheckMarkIcon}
                                      alt="Completed"
                                    />
                                  ) : (
                                    <img
                                      style={{
                                        width: '12px',
                                        height: '16px',
                                        marginRight: '11px',
                                        marginTop: '10px',
                                      }}
                                      src={IncompleteIcon}
                                      alt="Incomplete"
                                    />
                                  )}
                                  {pet.Name ?? pet.Species ?? 'New Animal'}
                                </Box>
                                <Typography sx={{ ml: 3 }} className={classes.grayLabelText}>
                                  {Applicant.FirstName} {Applicant.LastName}
                                </Typography>
                              </Box>
                              {
                                <Box sx={{ position: 'absolute', right: 16, height: '100%', display: 'flex', alignItems: 'center' }}>
                                  <IconButton
                                    onClick={() => {
                                      setSelectedPetUuid(pet.Uuid);
                                      setIsDeletePetModalOpen(true);
                                    }}
                                    sx={{ p: 0.25, mr: '-2px' }}
                                  >
                                    <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <img
                                        style={{
                                          cursor: 'pointer',
                                          width: '14px',
                                          height: '14px',
                                        }}
                                        src={DeleteIcon}
                                        alt="Delete"
                                      />
                                    </Icon>
                                  </IconButton>
                                  <IconButton
                                    onClick={() => {
                                      setSelectedPetUuid(pet.Uuid);
                                      history.push(`/resui/applications/${Application.Uuid}/pets/${pet.Uuid}${hasValidationQueryParam ? '?validation=true' : ''}`);
                                    }}
                                    sx={{ p: 0.25, mr: '-2px' }}
                                  >
                                    <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <img
                                        style={{
                                          cursor: 'pointer',
                                          width: '14px',
                                          height: '14px',
                                        }}
                                        src={EditIcon}
                                        alt="Edit"
                                      />
                                    </Icon>
                                  </IconButton>
                                </Box>
                              }
                            </Box>
                          );
                        })
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1.5px solid #F1F0F5' }}>
                          <Typography sx={{ mr: 3 }} className={classes.bodyText}>
                            Are there any {doesThisAppHaveSomePets ? 'additional' : ''} animals?
                          </Typography>
                          <RadioGroup value={hasNoPet == null ? null : !hasNoPet} row onChange={handleUpdateHasPet}>
                            <FormControlLabel value={false} control={<Radio size="small" />} sx={hasNoPet == null && hasValidationQueryParam ? { color: 'red' } : {}} label="No" />
                            <FormControlLabel value={true} control={<Radio size="small" />} sx={hasNoPet == null && hasValidationQueryParam ? { color: 'red' } : {}} label="Yes" />
                          </RadioGroup>
                        </Box>
                      )}
                      {otherApplicants
                        .filter((otherApplicant) => !!otherApplicant.Pets?.length)
                        .map((otherApplicant) => {
                          return otherApplicant.Pets?.map((pet) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', px: 2, py: 1, borderBottom: '1.5px solid #F1F0F5' }} key={pet.Uuid}>
                              <Box sx={{ width: '100%', pr: 6 }}>
                                <Typography sx={{ ml: 3 }}>{pet.Name ?? pet.Species ?? 'New Animal'}</Typography>
                                <Typography sx={{ ml: 3 }} className={classes.grayLabelText}>
                                  {otherApplicant.FirstName} {otherApplicant.LastName}
                                </Typography>
                              </Box>
                            </Box>
                          ));
                        })}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}

            {showVehicleSection && (
              <Grid item xs={12} sm={showPetSection ? 6 : 12} sx={{ display: 'flex', alignItems: 'center' }}>
                <Box className={classes.cardContainer}>
                  <Box sx={{ px: 1.5 }} className={classes.cardTopContainer}>
                    <Grid container sx={{ width: '100%' }}>
                      <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: isOnMobile ? 'center' : 'start', alignItems: 'center' }}>
                        <Typography className={classes.cardLabelText}>
                          Vehicles
                          <Tooltip title="Details about cars, trucks, and/or other vehicles that will be near or on the property." placement="right">
                            <HelpIcon sx={{ ml: 0.5, fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
                          </Tooltip>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: isOnMobile ? 'center' : 'end', alignItems: 'center' }}>
                        <Button
                          type="button"
                          className={classes.wizardAddNewBtn}
                          onClick={() => {
                            handleCreateNewVehicleRecord();
                          }}
                        >
                          <Typography className={classes.wizardAddNewBtnText} sx={{ margin: '0 10px' }}>
                            {`Add New`}
                          </Typography>
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box sx={{ height: 280, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        marginTop: '1px',
                        height: '100%',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                          width: 7,
                        },
                        '&::-webkit-scrollbar-track': {
                          boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                          borderRadius: '24px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'darkgrey',
                          borderRadius: '24px',
                        },
                      }}
                    >
                      {applicantAllVehicleRecords.length ? (
                        applicantAllVehicleRecords?.map((vehicle) => {
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', px: 2, py: 1, borderBottom: '1.5px solid #F1F0F5' }} key={vehicle.Uuid}>
                              <Box sx={{ width: '100%', pr: 6 }}>
                                <Box sx={{ display: 'flex' }}>
                                  {props.vehiclesValidator?.IsEntireObjectComplete(vehicle) ? (
                                    <img
                                      style={{
                                        width: '12px',
                                        marginRight: '11px',
                                        marginTop: '10px',
                                      }}
                                      src={CheckMarkIcon}
                                      alt="Completed"
                                    />
                                  ) : (
                                    <img
                                      style={{
                                        width: '12px',
                                        height: '16px',
                                        marginRight: '11px',
                                        marginTop: '10px',
                                      }}
                                      src={IncompleteIcon}
                                      alt="Incomplete"
                                    />
                                  )}
                                  {vehicle.Year} {vehicle.Make} {vehicle.Model} {!vehicle.Year && !vehicle.Make && !vehicle.Model ? 'New Vehicle' : ''}
                                </Box>

                                <Typography sx={{ ml: 3 }} className={classes.grayLabelText}>
                                  {Applicant.FirstName} {Applicant.LastName}
                                </Typography>
                              </Box>
                              {
                                <Box sx={{ position: 'absolute', right: 16, height: '100%', display: 'flex', alignItems: 'center' }}>
                                  <IconButton
                                    onClick={() => {
                                      setSelectedVehicleUuid(vehicle.Uuid);
                                      setIsDeleteVehicleModalOpen(true);
                                    }}
                                    sx={{ p: 0.25, mr: '-2px' }}
                                  >
                                    <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <img
                                        style={{
                                          cursor: 'pointer',
                                          width: '14px',
                                          height: '14px',
                                        }}
                                        src={DeleteIcon}
                                        alt="Delete"
                                      />
                                    </Icon>
                                  </IconButton>
                                  <IconButton
                                    onClick={() => {
                                      setSelectedVehicleUuid(vehicle.Uuid);
                                      history.push(`/resui/applications/${Application.Uuid}/vehicles/${vehicle.Uuid}${hasValidationQueryParam ? '?validation=true' : ''}`);
                                    }}
                                    sx={{ p: 0.25, mr: '-2px' }}
                                  >
                                    <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <img
                                        style={{
                                          cursor: 'pointer',
                                          width: '14px',
                                          height: '14px',
                                        }}
                                        src={EditIcon}
                                        alt="Edit"
                                      />
                                    </Icon>
                                  </IconButton>
                                </Box>
                              }
                            </Box>
                          );
                        })
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1.5px solid #F1F0F5' }}>
                          <Typography sx={{ mr: 3 }} className={classes.bodyText}>
                            Are there any {doesThisAppHaveSomeVehicles ? 'additional' : ''} vehicles?
                          </Typography>
                          <RadioGroup value={hasNoVehicle == null ? null : !hasNoVehicle} row onChange={handleUpdateHasVehicle}>
                            <FormControlLabel value={false} control={<Radio size="small" />} sx={hasNoVehicle == null && hasValidationQueryParam ? { color: 'red' } : {}} label="No" />
                            <FormControlLabel value={true} control={<Radio size="small" />} sx={hasNoVehicle == null && hasValidationQueryParam ? { color: 'red' } : {}} label="Yes" />
                          </RadioGroup>
                        </Box>
                      )}
                      {otherApplicants
                        .filter((otherApplicant) => !!otherApplicant.Vehicles?.length)
                        .map((otherApplicant) => {
                          return otherApplicant.Vehicles?.map((oav) => {
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', px: 2, py: 1, borderBottom: '1.5px solid #F1F0F5' }} key={oav.Uuid}>
                                <Box sx={{ width: '100%', pr: 6 }}>
                                  <Typography sx={{ ml: 3 }}>
                                    {oav.Year} {oav.Make} {oav.Model} {!oav.Year && !oav.Make && !oav.Model ? 'New Vehicle' : ''}
                                  </Typography>
                                  <Typography sx={{ ml: 3 }} className={classes.grayLabelText}>
                                    {otherApplicant.FirstName}
                                    {otherApplicant.LastName}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          });
                        })}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {isSpinnerModalOpen && (
        <SpinnerModal
          width="472px"
          height="523px"
          openSpinnerModal={isSpinnerModalOpen}
          fastSpinner={true} //get the loader to complete faster
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => {
            setIsSpinnerModalOpen(false);
            setSpinnerStatus(null);
          }}
        />
      )}
      {isDeletePetModalOpen && <DeleteModal open={isDeletePetModalOpen} confirmCallback={handleConfirmDeletePetModal} closeCallback={() => setIsDeletePetModalOpen(false)} />}
      {isDeleteVehicleModalOpen && <DeleteModal open={isDeleteVehicleModalOpen} confirmCallback={handleConfirmDeleteVehicleModal} closeCallback={() => setIsDeleteVehicleModalOpen(false)} />}
      {showPetPolicy && (
        <Dialog
          open={showPetPolicy}
          scroll={'paper'}
          maxWidth={'sm'}
          aria-labelledby="scroll-dialog-title"
          aria-describedby="scroll-dialog-description"
          PaperProps={{ style: { pointerEvents: 'auto', borderRadius: '24px' } }}
          sx={{ '& .MuiDialog-paper': { width: 759, maxHeight: 615 } }}
          onClose={() => setShowPetPolicy(false)}
        >
          <DialogTitle id="scroll-dialog-title" sx={{ alignSelf: 'center', justifyContent: 'flex-end', width: '100%', display: 'flex', '& p': { mt: 0, mb: 0 } }}>
            <p>{propertyGroupDetails?.CompanyName} Animal Policies</p>
            <IconButton sx={{ marginLeft: '140px', p: 0 }} onClick={() => setShowPetPolicy(false)}>
              <img src={CancelIcon} alt="cancel icon" width="26px" height="26px" />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              mx: 2,
              px: 1,
              mb: 5,
              '&::-webkit-scrollbar': {
                width: 7,
              },
              '&::-webkit-scrollbar-track': {
                boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                borderRadius: '24px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'darkgrey',
                borderRadius: '24px',
              },
            }}
          >
            <Box id="scroll-dialog-description" tabIndex={-1} className={classes.bodyText}>
              <div dangerouslySetInnerHTML={{ __html: (propertyGroupDetails?.ApplicationsSettings?.ApplicationsPetPolicyText ?? '').replace(/<\/?html>|<\/?body>/g, '') }} />
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}
